import { Fragment, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconInfoCircle,
  IconKeyboard,
  IconMessageCircle,
  IconPlayerPlay,
  IconRecordMail,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import type { Edge, Node } from '@xyflow/react';
import type { LanguageConfig } from '@legacy-shared/widgets/core/types';
import { useHandleAction } from '@/components/flow-editor/handle-action-context';
import { VariableTextarea } from '@/components/flow-editor/variable-textarea';
import { actionOptions, allowedActionTypes } from '@/lib/flow-editor/action-options';
import { isZeroRouteConfigured } from '@/widgets/handle-action-rules';
import { widgetOutputs, widgetRegistry } from '@/widgets/registry';
import type { CrmVariableMapping } from '@/widgets/variables';
import { getAvailableVariables } from '@/widgets/variables';
import type {
  ConfigPanelProps,
  FlowEdge,
  FlowNode,
  HandleActionType,
  HandleDefinition,
  LanguageOption,
  VariableInfo,
  WidgetDefinition,
  WidgetNodeData,
} from '@/widgets/types';

const widgetIcons: Record<string, typeof IconMessageCircle> = {
  start: IconPlayerPlay,
  say: IconMessageCircle,
  gather: IconKeyboard,
  recording: IconRecordMail,
};

const messageLabel = (
  <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    Message
    <Tooltip label="Optional message spoken before the action" multiline w={200} withArrow>
      <IconInfoCircle size={14} style={{ color: 'var(--mantine-color-dimmed)', cursor: 'help' }} />
    </Tooltip>
  </Box>
);

type OutputRowProps = {
  nodeId: string;
  output: HandleDefinition;
  availableVariables?: VariableInfo[];
  languages?: LanguageOption[];
  defaultLanguage?: string;
};

function OutputRow({ nodeId, output, availableVariables, languages, defaultLanguage }: OutputRowProps) {
  const { getHandleAction, setHandleAction, isZeroRouteAllowed, isOfficeClosedAllowed } = useHandleAction();
  const action = getHandleAction(nodeId, output.id);

  return (
    <Box style={action ? { display: 'flex', flexDirection: 'column', flex: 1 } : undefined}>
      <Stack gap="xs" style={action ? { flex: 1 } : undefined}>
        <Select
          label={output.label}
          value={action?.type ?? ''}
          onChange={(v) => setHandleAction(nodeId, output.id, v ? { type: v as HandleActionType } : undefined)}
          data={actionOptions(allowedActionTypes(isZeroRouteAllowed, isOfficeClosedAllowed, nodeId, output.id), action)}
        />
        {action && (
          <VariableTextarea
            label={messageLabel}
            value={action.message ?? ''}
            onChange={(v) =>
              setHandleAction(nodeId, output.id, {
                ...action,
                message: (typeof v === 'string' ? v.trim().length > 0 : Object.values(v).some((s) => s.trim()))
                  ? v
                  : undefined,
              })
            }
            availableVariables={availableVariables}
            languages={languages}
            defaultLanguage={defaultLanguage}
            minRows={2}
            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
            styles={{ wrapper: { flex: 1 }, input: { height: '100%' } }}
          />
        )}
      </Stack>
    </Box>
  );
}

type OutputActionsSectionProps = {
  nodeId: string;
  outputs: HandleDefinition[];
  availableVariables?: VariableInfo[];
  languages?: LanguageOption[];
  defaultLanguage?: string;
};

function OutputActionsSection({
  nodeId,
  outputs,
  availableVariables,
  languages,
  defaultLanguage,
}: OutputActionsSectionProps) {
  return (
    <Stack gap="md" style={{ flex: 1 }}>
      <Text size="sm" c="dimmed">
        Assign an action to an output handle instead of connecting it to another widget.
      </Text>
      {outputs.map((output, i) => (
        <Fragment key={output.id}>
          {i > 0 && <Divider />}
          <OutputRow
            nodeId={nodeId}
            output={output}
            availableVariables={availableVariables}
            languages={languages}
            defaultLanguage={defaultLanguage}
          />
        </Fragment>
      ))}
    </Stack>
  );
}

function PanelHeader({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
      <Group justify="space-between" wrap="nowrap">
        {children}
        <ActionIcon variant="subtle" onClick={onClose}>
          <IconX size={16} />
        </ActionIcon>
      </Group>
    </Box>
  );
}

function UnknownWidgetPanel({ nodeType, onClose }: { nodeType: string | undefined; onClose: () => void }) {
  return (
    <Stack gap={0} h="100%">
      <PanelHeader onClose={onClose}>
        <Text fw={600}>Unknown Widget</Text>
      </PanelHeader>
      <Box p="md">
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Widget type &quot;{nodeType}&quot; is not registered.
        </Alert>
      </Box>
    </Stack>
  );
}

function DeleteWidgetFooter({ onDelete }: { onDelete: () => void }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      {confirming ? (
        <Stack gap="xs">
          <Text size="sm" ta="center" c="dimmed">
            Delete this widget?
          </Text>
          <Group grow>
            <Button variant="subtle" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={onDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      ) : (
        <Button
          variant="subtle"
          color="red"
          fullWidth
          leftSection={<IconTrash size={16} />}
          onClick={() => setConfirming(true)}
        >
          Delete Widget
        </Button>
      )}
    </Box>
  );
}

const panelBodyStyle = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' } as const;

type WidgetPanelBodyProps = {
  widget: WidgetDefinition;
  nodeId: string;
  outputs: HandleDefinition[];
  configPanelProps: ConfigPanelProps;
};

function WidgetPanelBody({ widget, nodeId, outputs, configPanelProps }: WidgetPanelBodyProps) {
  const configTabs = widget.ui.configTabs;

  if (!configTabs && outputs.length === 0) {
    return (
      <Box p="md" style={panelBodyStyle}>
        <widget.ui.configPanel {...configPanelProps} />
      </Box>
    );
  }

  return (
    <Tabs
      defaultValue={configTabs?.[0]?.value ?? 'config'}
      keepMounted
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Tabs.List>
        {configTabs ? (
          configTabs.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))
        ) : (
          <Tabs.Tab value="config">Config</Tabs.Tab>
        )}
        {outputs.length > 0 && <Tabs.Tab value="outputs">Outputs</Tabs.Tab>}
      </Tabs.List>

      {configTabs ? (
        configTabs.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} p="md" style={panelBodyStyle}>
            <tab.panel {...configPanelProps} />
          </Tabs.Panel>
        ))
      ) : (
        <Tabs.Panel value="config" p="md" style={panelBodyStyle}>
          <widget.ui.configPanel {...configPanelProps} />
        </Tabs.Panel>
      )}

      {outputs.length > 0 && (
        <Tabs.Panel value="outputs" p="md" style={panelBodyStyle}>
          <OutputActionsSection
            nodeId={nodeId}
            outputs={outputs}
            availableVariables={configPanelProps.availableVariables}
            languages={configPanelProps.languages}
            defaultLanguage={configPanelProps.defaultLanguage}
          />
        </Tabs.Panel>
      )}
    </Tabs>
  );
}

type ConfigurationPanelProps = {
  node: Node<WidgetNodeData>;
  nodes: Node<WidgetNodeData>[];
  edges: Edge[];
  onConfigChange: (config: Record<string, unknown>) => void;
  onLabelChange: (label: string) => void;
  onClose: () => void;
  onDelete?: () => void;
  languageConfig?: LanguageConfig;
  crmVariables?: CrmVariableMapping[];
};

type WidgetConfigurationPanelProps = ConfigurationPanelProps & { widget: WidgetDefinition };

function WidgetConfigurationPanel({
  widget,
  node,
  nodes,
  edges,
  onConfigChange,
  onLabelChange,
  onClose,
  onDelete,
  languageConfig,
  crmVariables,
}: WidgetConfigurationPanelProps) {
  const availableVariables = useMemo(() => {
    const flowNodes: FlowNode[] = nodes.map((n) => ({
      id: n.id,
      type: n.type || 'unknown',
      position: n.position,
      data: n.data,
    }));
    const flowEdges: FlowEdge[] = edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? undefined,
      targetHandle: e.targetHandle ?? undefined,
    }));

    return getAvailableVariables(flowNodes, flowEdges, node.id, crmVariables);
  }, [nodes, edges, node.id, crmVariables]);

  const languages = useMemo(
    () => languageConfig?.languages.map((l) => ({ code: l.code, label: l.label })),
    [languageConfig],
  );

  const zeroRouteEnabled = useMemo(() => isZeroRouteConfigured(nodes.find((n) => n.type === 'start')), [nodes]);

  const Icon = widgetIcons[node.type ?? ''] ?? IconMessageCircle;
  const errors = node.data.errors ?? [];
  const outputs = widgetOutputs(node.type ?? '', node.data.config) ?? [];

  return (
    <Stack gap={0} h="100%">
      <PanelHeader onClose={onClose}>
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon variant="light" size="md" color="indigo">
            <Icon size={14} />
          </ThemeIcon>
          <TextInput
            size="sm"
            variant="unstyled"
            placeholder={widget.name}
            value={node.data.label ?? ''}
            onChange={(e) => onLabelChange(e.currentTarget.value)}
            styles={{ input: { fontWeight: 600, padding: 0, minHeight: 'unset', height: 'auto' } }}
            style={{ flex: 1, minWidth: 0 }}
          />
        </Group>
      </PanelHeader>

      {errors.length > 0 && (
        <Box p="md" pb={0}>
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            <Stack gap={4}>
              {errors.map((error, i) => (
                <Text key={i} size="sm">
                  {error}
                </Text>
              ))}
            </Stack>
          </Alert>
        </Box>
      )}

      <WidgetPanelBody
        widget={widget}
        nodeId={node.id}
        outputs={outputs}
        configPanelProps={{
          config: node.data.config,
          onChange: onConfigChange,
          errors,
          availableVariables,
          zeroRouteEnabled,
          languages,
          defaultLanguage: languageConfig?.defaultLanguage,
        }}
      />

      {onDelete && <DeleteWidgetFooter onDelete={onDelete} />}
    </Stack>
  );
}

export function ConfigurationPanel(props: ConfigurationPanelProps) {
  const widget = widgetRegistry.get(props.node.type ?? '');

  if (!widget) return <UnknownWidgetPanel nodeType={props.node.type} onClose={props.onClose} />;

  return <WidgetConfigurationPanel {...props} widget={widget} />;
}
